import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { baseAxios } from "../../utils/config";
import { toast } from "react-hot-toast";
import {
  ClientNotesList,
  appointmentOptionLabel,
} from "../../components/client-notes/clientNotes";
import { primaryBtnSx, secondaryBtnSx } from "../Scheduler/schedulerDialogStyles";

export default function NoteDialog({
  open,
  onClose,
  client,
  appointments: appointmentsProp,
  initialAppointmentId = null,
  onSaved,
}) {
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [appointmentId, setAppointmentId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const linkableAppointments = useMemo(
    () =>
      (appointmentsProp ?? appointments).filter(
        (appointment) => appointment.appointmentId
      ),
    [appointments, appointmentsProp]
  );

  useEffect(() => {
    if (!open) return;
    setNote("");
    setAppointmentId(
      initialAppointmentId ? String(initialAppointmentId) : ""
    );
  }, [open, client?.client_id, initialAppointmentId]);

  useEffect(() => {
    if (!open || !client?.client_id || appointmentsProp) {
      setAppointments([]);
      return;
    }
    setLoadingAppointments(true);
    baseAxios
      .get(`/scheduler/pastAppointments/${client.client_id}`)
      .then((res) => res.status === 200 && setAppointments(res.data || []))
      .catch(() => toast.error("Error fetching appointments", { duration: 3000 }))
      .finally(() => setLoadingAppointments(false));
  }, [open, client?.client_id, appointmentsProp]);

  useEffect(() => {
    if (!open || !client?.client_id) {
      setNotes([]);
      return;
    }
    setLoadingNotes(true);
    baseAxios
      .get(`/clients/${client.client_id}/notes`)
      .then((res) => res.status === 200 && setNotes(res.data?.notes || []))
      .catch(() => toast.error("Error fetching notes", { duration: 3000 }))
      .finally(() => setLoadingNotes(false));
  }, [open, client?.client_id]);

  const visibleNotes = appointmentId
    ? notes.filter((item) => String(item.schedule_entry_id) === appointmentId)
    : notes;

  const handleSave = async () => {
    const text = note.trim();
    if (!text || !client?.client_id) return;
    setSaving(true);
    try {
      const res = await baseAxios.post(`/clients/${client.client_id}/notes`, {
        note: text,
        schedule_entry_id: appointmentId ? Number(appointmentId) : null,
      });
      if (res.status === 201) {
        setNotes((current) => [res.data.note, ...current]);
        setNote("");
        toast.success("Note saved", { duration: 2500 });
        onSaved?.();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save note", {
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const clientName = [client?.client_first_name, client?.client_last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 2.5,
          py: 1.5,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="inherit">
              Add note
            </Typography>
            {clientName && (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                {clientName}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={saving}
            size="small"
            sx={{
              color: "primary.main",
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <TextField
          select
          fullWidth
          label="Appointment"
          value={appointmentId}
          onChange={(event) => setAppointmentId(event.target.value)}
          helperText="Optional"
          disabled={loadingAppointments}
          sx={{ mt: 1, mb: 2 }}
        >
          <MenuItem value="">None — general note</MenuItem>
          {linkableAppointments.map((appointment) => (
            <MenuItem
              key={appointment.appointmentId}
              value={String(appointment.appointmentId)}
            >
              {appointmentOptionLabel(appointment)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          required
          multiline
          minRows={4}
          label="Note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Write a note"
        />
        <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
          {appointmentId ? "Notes for this appointment" : "Notes"}
        </Typography>
        <ClientNotesList
          notes={visibleNotes}
          loading={loadingNotes}
          emptyText="No notes yet."
          minHeight={120}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2, gap: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={secondaryBtnSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !note.trim()}
          sx={primaryBtnSx}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
